import type { ReactNode } from "react";
import { useI18n } from "../i18n/index.js";
import { cx } from "./cx.js";

export type BadgeTone = "neutral" | "accent" | "info" | "success" | "warning" | "danger";

/** Maps lifecycle / job / role vocab to a tone so every status reads the same way across pages. */
export function toneFor(status: string): BadgeTone {
  switch (status) {
    case "accepted":
    case "succeeded":
    case "active":
      return "success";
    case "proposed":
    case "queued":
    case "running":
    case "pending":
    case "admin":
      return "info";
    case "rejected":
    case "failed":
    case "dead":
      return "danger";
    case "superseded":
    case "deprecated":
      return "warning";
    case "owner":
      return "accent";
    default:
      return "neutral";
  }
}

export function Badge({ tone, children, className, dot }: { tone?: BadgeTone | undefined; children: ReactNode; className?: string | undefined; dot?: boolean | undefined }) {
  const t = tone ?? (typeof children === "string" ? toneFor(children) : "neutral");
  return (
    <span className={cx("dt-badge", `dt-badge--${t}`, className)}>
      {dot && <span className="dt-badge__dot" />}
      {children}
    </span>
  );
}

/** Status badge with a live dot for in-flight states. The tone stays keyed to the raw value. */
export function StatusBadge({ status }: { status: string }) {
  const { tOr } = useI18n();
  const live = status === "queued" || status === "running" || status === "pending";
  return (
    <Badge tone={toneFor(status)} dot className={live ? "dt-badge--live" : undefined}>
      {tOr(`status.${status}`, status.replaceAll("_", " "))}
    </Badge>
  );
}
